import React from 'react';
import { Route, Switch } from 'react-router-dom';

import { AdminLoginPage } from '@/pages/System/AdminLogin';
import { EnvironmentService } from '@/services/Environment';

import { PrivateRoute } from './PrivateRoute';
import { Redirect } from './Redirect';
import { ROUTES } from './route-names';
import { ScrollToTop } from './ScrollToTop';

const LogInPage = React.lazy(() => import('@/pages/Auth/LogIn'));
const CreateAccount = React.lazy(() => import('@/pages/Auth/CreateAccount'));
const SelectCompanyPage = React.lazy(() => import('@/pages/Auth/SelectCompany'));
const ActivityListPage = React.lazy(() => import('@/pages/Dashboard/ActivityList'));
const ActivityDetails = React.lazy(() => import('@/pages/Dashboard/ActivityDetails'));
const CreateLeadPage = React.lazy(() => import('@/pages/Lead/Create'));
const EditProfilePage = React.lazy(() => import('@/pages/Profile/Edit'));
const ProfilePage = React.lazy(() => import('@/pages/Profile/View'));
const SalesResourcesPage = React.lazy(() => import('@/pages/SalesResources'));
const PrivacyPolicyPage = React.lazy(() => import('@/pages/Public/PrivacyPolicy'));
const TermsAndConditionsPage = React.lazy(() => import('@/pages/Public/TermsAndConditions'));
const TermsOfPurchasePage = React.lazy(() => import('@/pages/Public/TermsOfPurchase'));
const WebToLeadFormPage = React.lazy(() => import('@/pages/Public/WebToLeadForm'));
const LogoutPage = React.lazy(() => import('@/pages/Auth/Logout'));
const PageNotFound = React.lazy(() => import('@/pages/System/PageNotFound'));
const ForgotPassword = React.lazy(() => import('@/pages/Auth/ForgotPassword'));
const ChangePassword = React.lazy(() => import('@/pages/Auth/ChangePassword'));
const ManageUsers = React.lazy(() => import('@/pages/Admin/ManageUsers'));
const SiteSurveyPage = React.lazy(() => import('@/pages/SiteSurvey'));
const AccountSettings = React.lazy(() => import('@/pages/Admin/CompanyPricing'));
const EstimatedSolarBOM = React.lazy(() => import('@/pages/Lead/EstimatedSolarBOM'));
const ProposalPricing = React.lazy(() => import('@/pages/ProposalPricing'));

const { Auth, Public, Private, System } = ROUTES;

export const Router = () => (
  <>
    <ScrollToTop />
    <Switch>
      {/*Public routes*/}
      <Route component={LogInPage} exact path={Auth.LogIn} />
      <Redirect exact from={Auth.Welcome} to={Auth.LogIn} />
      <Route component={LogoutPage} exact path={Auth.Logout} />
      <Route component={CreateAccount} exact path={Auth.CreateAccount} />
      <Route component={ChangePassword} exact path={Auth.ChangePassword} />
      <Route component={CreateAccount} exact path={Auth.CheckInvitation} />
      <Route component={SelectCompanyPage} path={Auth.SelectCompany} />
      <Route component={ForgotPassword} path={Auth.ForgotPassword} />
      <Route component={TermsOfPurchasePage} exact path={Public.TermsOfPurchase} />
      <Route component={TermsAndConditionsPage} exact path={Public.TermsAndConditions} />
      <Route component={PrivacyPolicyPage} exact path={Public.PrivacyPolicy} />
      <Route exact path={Public.WebToLeadForm}>
        <WebToLeadFormPage env="PROD" />
      </Route>

      {!EnvironmentService.isPROD && (
        <Route exact path={Public.TEMPORARY_SFDC_DEV}>
          <WebToLeadFormPage env="DEV" />
        </Route>
      )}
      {!EnvironmentService.isPROD && (
        <Route exact path={Public.TEMPORARY_SFDC_QA}>
          <WebToLeadFormPage env="QA" />
        </Route>
      )}
      {!EnvironmentService.isPROD && (
        <Route exact path={Public.TEMPORARY_SFDC_STG}>
          <WebToLeadFormPage env="STG" />
        </Route>
      )}

      {/* New Site Survey Task card*/}
      <PrivateRoute component={SiteSurveyPage} path={Private.newSiteSurveyTaskCard.welcome} />
      <PrivateRoute
        component={SiteSurveyPage}
        exact
        path={Private.newSiteSurveyTaskCard.submitSurvey}
      />

      {/*Private routes*/}
      <Redirect from="/activity/leads/create" to={Private.Dashboard.Lead.Create} />
      <PrivateRoute component={CreateLeadPage} path={Private.Dashboard.Lead.Create} />

      <Redirect from="/activity/details/:detailsTarget" to={Private.Dashboard.ActivityDetails} />
      <Redirect
        exact
        from="/dashboard/details/:detailsTarget"
        to={Private.Dashboard.ActivityDetails}
      />
      <PrivateRoute component={ProposalPricing} path={Private.ProposalPricing.page} />
      <PrivateRoute component={ActivityDetails} path={Private.Dashboard.ActivityDetails} />
      <PrivateRoute component={EstimatedSolarBOM} path={Private.EstimatedSolarBom} />

      <Redirect from="/activity" to={Private.Dashboard.ActivityList} />
      <PrivateRoute component={ActivityListPage} path={Private.Dashboard.ActivityList} />

      <PrivateRoute component={EditProfilePage} path={Private.Profile.Edit} />
      <PrivateRoute component={ProfilePage} path={Private.Profile.View} />
      <PrivateRoute component={SalesResourcesPage} path={Private.SalesResources} />
      <PrivateRoute component={ManageUsers} path={Private.ManageUsers} />
      <PrivateRoute component={AccountSettings} path={Private.AccountSettings} />

      {/*Site Survey*/}
      <PrivateRoute component={SiteSurveyPage} path={Private.siteSurvey.welcome} />
      <PrivateRoute component={SiteSurveyPage} exact path={Private.siteSurvey.submitSurvey} />

      {/*System routes*/}
      <Route component={AdminLoginPage} exact path={System.AdminLogin} />

      {/*Fallback route*/}
      <Route component={PageNotFound} />
    </Switch>
  </>
);
